/**
 * The man-overboard readout — a persistent, high-contrast strip across the top
 * of the screen the whole time a MOB is live. Report fb-ms1ikxp0-2.
 *
 * When someone's in the water the one thing the helm needs, glanceable, is
 * "where is the mark and how long ago". So this shows, from the boat's current
 * position to the MOB LAST-KNOWN position: the bearing and the distance, both
 * live as the boat moves, the time the MOB was activated, and an elapsed timer
 * ticking up since. It sits at the very top, full width, in MOB red so it can't
 * be missed, and it reads the same colours day or night because it carries its
 * own, rather than borrowing the chart palette.
 *
 * Tapping the strip recentres the chart on the MOB last-known position, so the
 * helm can jump straight back to the mark. Beyond that it's a readout: the drift
 * estimate and the search patterns live behind a second press of the MOB button.
 * This is the number the helm steers by.
 * Hidden entirely when no MOB is active, so it never adds clutter at rest.
 */
import { useEffect, useState } from 'react';
import { useChartPlotterStore } from '@/store';
import { useShellStore } from '@/store/shell';
import { useSafetyStore } from '@/store/safety';
import { useBearingDisplayStore } from '@/store/bearingDisplay';
import { checkMob } from '@/safety';
import { formatBearing, formatDistance } from '@/nav';

/** Local wall-clock time of activation, HH:MM (24h), from the drop timestamp. */
function activationTime(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Elapsed since the drop as mm:ss, rolling to h:mm:ss past the hour. */
function elapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export function MobReadout() {
  const mob = useSafetyStore((s) => s.mob);
  const position = useChartPlotterStore((s) => s.ownship.position);
  const requestMobRecentre = useShellStore((s) => s.requestMobRecentre);
  // Resubscribe so the bearing reformats the instant the True/Magnetic
  // reference or the variation changes (formatBearing reads the global setting).
  useBearingDisplayStore((s) => s.mode);
  useBearingDisplayStore((s) => s.variationDeg);

  // One-second tick so the elapsed timer counts and the bearing/distance keep up
  // even when the boat is momentarily still. Only runs while a MOB is active.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!mob) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [mob]);

  if (!mob) return null;

  const status = checkMob(mob, position, Date.now());
  const rangeBearing = status
    ? `${formatBearing(status.bearingDeg)}  ·  ${formatDistance(status.rangeNm)}`
    : 'No fix — bearing and range unavailable';

  return (
    // A button, not a div, so it's tappable, keyboard-focusable and announced as a
    // control. It keeps the live region so the range/bearing is still read aloud as
    // it updates, and the same classes so it looks identical.
    <button
      type="button"
      className="acp-mob-readout"
      role="status"
      aria-live="polite"
      aria-label="Man overboard readout — tap to recentre the chart on the mark"
      onClick={() => requestMobRecentre()}
    >
      <span className="acp-mob-readout-tag">MOB</span>
      <span className="acp-mob-readout-rb">{rangeBearing}</span>
      <span className="acp-mob-readout-meta">
        <span className="acp-mob-readout-since">Activated {activationTime(mob.droppedAt)}</span>
        <span className="acp-mob-readout-elapsed" aria-label="Time since man overboard">
          {elapsed(Date.now() - mob.droppedAt)}
        </span>
      </span>
    </button>
  );
}
